import { useTranslation } from 'react-i18next';
import type { Anime } from '@/mocks/anime';

interface AnimeDetailProps {
  anime: Anime;
}

export default function AnimeDetail({ anime }: AnimeDetailProps) {
  const { t } = useTranslation();

  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="px-4 md:px-6 max-w-6xl mx-auto">
        <div className="mb-2">
          <span className="text-xs text-foreground-500 font-medium tracking-wider uppercase">
            {t('动漫详情')}
          </span>
        </div>

        <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 leading-tight mb-4 max-w-2xl">
          {anime.title}
        </h1>

        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-10 max-w-3xl">
          {anime.description}
        </p>

        <div className="flex flex-col md:flex-row gap-6 mb-10">
          <div className="w-full md:w-[40%]">
            <div className="rounded-xl overflow-hidden">
              <img
                src={anime.cover}
                alt={`${anime.title} 剧照`}
                title={`${anime.title} - 樱花动漫`}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
          <div className="w-full md:w-[60%]">
            <div className="rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20anime%20style%20cinematic%20banner%20with%20soft%20golden%20sunlight%20streaming%20through%20cherry%20blossom%20trees%2C%20dreamy%20Japanese%20or%20Chinese%20fantasy%20landscape%2C%20warm%20coral%20and%20amber%20tones%2C%20cinematic%20wide%20composition%2C%20film%20grain%20texture%2C%20artistic%20anime%20background&width=1200&height=600&seq=anime-detail-banner&orientation=landscape"
                alt={`${anime.title} 横幅`}
                title={`${anime.title}视觉图`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-heading text-lg text-foreground-900 mb-4">{t('基本信息')}</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0">{t('评分')}</span>
                <div className="flex items-center gap-1">
                  <span className="text-accent-500 font-bold text-lg">{anime.rating}</span>
                  <span className="text-foreground-400 text-xs">/ 10</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0">{t('话数')}</span>
                <span className="text-sm text-foreground-900">{anime.episodes} {t('话')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0">{t('状态')}</span>
                <span className={`text-sm px-2 py-0.5 rounded-full whitespace-nowrap ${
                  anime.status === '已完结' ? 'bg-background-200 text-foreground-700' : 'bg-primary-100 text-primary-700'
                }`}>
                  {anime.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0">{t('类型标签')}</span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {anime.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-background-200 text-foreground-600 px-2 py-0.5 rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0">{t('上映时间')}</span>
                <span className="text-sm text-foreground-900">{anime.year}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0">{t('地区')}</span>
                <span className="text-sm text-foreground-900">{anime.region}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-lg text-foreground-900 mb-4">{t('制作团队')}</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0">{t('导演')}</span>
                <span className="text-sm text-foreground-900">{anime.director}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0">{t('动画制作')}</span>
                <span className="text-sm text-foreground-900">{anime.studio}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0 pt-0.5">{t('声优')}</span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {anime.voiceActors.map((actor) => (
                    <span key={actor} className="text-sm text-foreground-900">{actor}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 w-20 flex-shrink-0">{t('播放量')}</span>
                <span className="text-sm text-foreground-900">
                  {(anime.views / 10000).toFixed(0)}{t('万次播放')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}